import type { CSSProperties, ReactNode } from 'react';

/**
 * Small line drawings for the «تصفّح حسب نوع العقار» cards, one per type, in
 * the manner of the action cards above them (owner, 2026-10-07: the old tiles,
 * an icon in a rounded square, read as AI-made). Each shows what its card says:
 * a block of flats, a villa, a surveyed plot, an office tower, a building with
 * shops below, a rest house with its pool and palm.
 *
 * - Drawn in `currentColor` (the card sets brand teal), so dark mode follows.
 * - The logo's yellow lights a few windows at rest; hovering the card (the
 *   `group`) lights the rest one after another. Subtle on purpose: THEME.md
 *   keeps the board's yellow to small marks.
 * - 224 x 140, ground at y = 132. The card crops the bottom 8px, so the scene
 *   can rise on hover without showing an edge.
 * - Decorative: the card's text carries the meaning (aria-hidden on the svg).
 */

const LIT = '#FFC107';
const STROKE = { stroke: 'currentColor', strokeWidth: 1.75, strokeLinejoin: 'round', strokeLinecap: 'round' } as const;
const HOVER_LIGHT = 'opacity-0 transition-opacity duration-300 group-hover:opacity-100 motion-reduce:transition-none';

function Scene({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <svg viewBox="0 0 224 140" className={className} aria-hidden="true" focusable="false">
      {children}
      {/* The ground the scene stands on; its lower edge is under the card's. */}
      <rect x="0" y="132" width="224" height="8" fill="currentColor" fillOpacity="0.05" />
      <line x1="8" y1="132" x2="216" y2="132" stroke="currentColor" strokeOpacity="0.28" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/** A window: lit at rest, lit on hover (after `delay` ms), or plain glass. */
function Win({ x, y, w = 12, h = 10, lit = false, hover = false, delay = 0 }: {
  x: number; y: number; w?: number; h?: number; lit?: boolean; hover?: boolean; delay?: number;
}) {
  const style: CSSProperties | undefined = hover ? { transitionDelay: `${delay}ms` } : undefined;
  return (
    <>
      <rect x={x} y={y} width={w} height={h} rx="1.5" fill="currentColor" fillOpacity="0.14" />
      {(lit || hover) && (
        <rect x={x} y={y} width={w} height={h} rx="1.5" fill={LIT} className={lit ? undefined : HOVER_LIGHT} style={style} />
      )}
    </>
  );
}

function Palm({ x, top }: { x: number; top: number }) {
  // Trunk from the ground to the crown, then five fronds.
  return (
    <g fill="none" {...STROKE}>
      <path d={`M${x} 132 C ${x} ${top + 26}, ${x + 2} ${top + 12}, ${x + 4} ${top}`} />
      <path d={`M${x + 4} ${top} q -12 -7 -23 1`} />
      <path d={`M${x + 4} ${top} q -7 -12 -19 -12`} />
      <path d={`M${x + 4} ${top} q 2 -12 -1 -17`} />
      <path d={`M${x + 4} ${top} q 8 -11 19 -9`} />
      <path d={`M${x + 4} ${top} q 13 -3 21 7`} />
    </g>
  );
}

/** شقق: a block of flats beside a lower one, a tree at its door. */
export function ApartmentScene({ className }: { className?: string }) {
  const cols = [87, 106, 125];
  const rows = [40, 57, 74, 91];
  const atRest = new Set(['106-57', '87-91']);
  const order = ['125-40', '87-57', '106-74', '125-91', '87-40', '125-74'];
  return (
    <Scene className={className}>
      {/* the lower block behind */}
      <rect x="146" y="62" width="32" height="70" fill="currentColor" fillOpacity="0.04" stroke="currentColor" strokeOpacity="0.55" strokeWidth="1.5" />
      {[152, 165].flatMap((x) => [72, 88, 104].map((y) => (
        <rect key={`${x}-${y}`} x={x} y={y} width="8" height="8" rx="1" fill="currentColor" fillOpacity="0.12" />
      )))}
      {/* the main block, its stair room on the roof */}
      <rect x="120" y="21" width="16" height="9" fill="currentColor" fillOpacity="0.06" {...STROKE} />
      <rect x="78" y="30" width="68" height="102" fill="currentColor" fillOpacity="0.06" {...STROKE} />
      {rows.flatMap((y) => cols.map((x) => {
        const key = `${x}-${y}`;
        const hoverAt = order.indexOf(key);
        return <Win key={key} x={x} y={y} lit={atRest.has(key)} hover={hoverAt >= 0} delay={hoverAt * 70} />;
      }))}
      {/* balconies on the middle column */}
      <line x1="102" y1="69" x2="122" y2="69" {...STROKE} strokeWidth="1.25" />
      <line x1="102" y1="103" x2="122" y2="103" {...STROKE} strokeWidth="1.25" />
      {/* the entrance */}
      <rect x="105" y="113" width="14" height="19" rx="1.5" fill="currentColor" fillOpacity="0.16" {...STROKE} strokeWidth="1.5" />
      {/* a tree by the door */}
      <line x1="56" y1="132" x2="56" y2="114" {...STROKE} />
      <circle cx="56" cy="105" r="13" fill="currentColor" fillOpacity="0.1" {...STROKE} />
    </Scene>
  );
}

/** فلل: a two-storey villa with a flat parapet roof, a palm by the wall. */
export function VillaScene({ className }: { className?: string }) {
  return (
    <Scene className={className}>
      {/* the garden wall either side */}
      <path d="M18 132 V118 H50" fill="none" {...STROKE} strokeOpacity="0.55" />
      <path d="M174 118 H206 V132" fill="none" {...STROKE} strokeOpacity="0.55" />
      {/* ground floor, upper floor */}
      <rect x="50" y="78" width="124" height="54" fill="currentColor" fillOpacity="0.06" {...STROKE} />
      <rect x="70" y="46" width="84" height="32" fill="currentColor" fillOpacity="0.06" {...STROKE} />
      <line x1="66" y1="46" x2="158" y2="46" {...STROKE} strokeWidth="2.25" />
      <Win x={82} y={55} w={20} h={14} lit />
      <Win x={122} y={55} w={20} h={14} hover delay={140} />
      <Win x={62} y={94} w={26} h={18} hover delay={0} />
      <Win x={136} y={94} w={26} h={18} hover delay={70} />
      {/* the door, under a canopy; it lights first */}
      <line x1="98" y1="101" x2="126" y2="101" {...STROKE} strokeWidth="2.25" />
      <rect x="104" y="106" width="16" height="26" rx="1.5" fill="currentColor" fillOpacity="0.16" {...STROKE} strokeWidth="1.5" />
      <rect x="104" y="106" width="16" height="26" rx="1.5" fill={LIT} fillOpacity="0.85" className={HOVER_LIGHT} />
      <line x1="112" y1="106" x2="112" y2="132" {...STROKE} strokeWidth="1.25" />
      <Palm x={188} top={86} />
    </Scene>
  );
}

/** أراضٍ: a surveyed plot, its corners staked, a pin on it. */
export function LandScene({ className }: { className?: string }) {
  return (
    <Scene className={className}>
      {/* a few tufts of ground */}
      {[22, 34, 198].map((x) => (
        <path key={x} d={`M${x} 132 l3 -6 M${x + 4} 132 l2 -4`} fill="none" {...STROKE} strokeOpacity="0.4" strokeWidth="1.25" />
      ))}
      {/* the plot; its dashed edge marches while the card is hovered */}
      <path
        d="M44 128 L76 92 L172 92 L188 128 Z"
        fill="currentColor"
        fillOpacity="0.07"
        {...STROKE}
        strokeDasharray="6 5"
        className="pv-march"
      />
      {[[44, 128], [76, 92], [172, 92], [188, 128]].map(([cx, cy]) => (
        <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="3" style={{ fill: 'var(--pv-color-surface, #fff)' }} {...STROKE} strokeWidth="1.5" />
      ))}
      {/* the measured side */}
      <path d="M76 82 H172 M76 78 V86 M172 78 V86" fill="none" {...STROKE} strokeOpacity="0.5" strokeWidth="1.25" />
      {/* the pin, with the logo's yellow at its heart; it lifts on hover */}
      <g className="transition-transform duration-300 ease-out group-hover:-translate-y-1 motion-reduce:transition-none">
        <path
          d="M118 70 C 105 56, 104 46, 104 42 A 14 14 0 0 1 132 42 C 132 46, 131 56, 118 70 Z"
          style={{ fill: 'var(--pv-color-surface, #fff)' }}
          {...STROKE}
        />
        <circle cx="118" cy="42" r="5.5" fill={LIT} />
      </g>
    </Scene>
  );
}

/** مكاتب: a glass office tower over a low annex. */
export function OfficeScene({ className }: { className?: string }) {
  const floors = Array.from({ length: 9 }, (_, i) => 26 + i * 12);
  const panels: { x: number; y: number; lit?: boolean; at?: number }[] = [
    { x: 99, y: 38, lit: true },
    { x: 112, y: 74, lit: true },
    { x: 86, y: 50, at: 0 },
    { x: 125, y: 26, at: 1 },
    { x: 99, y: 86, at: 2 },
    { x: 112, y: 110, at: 3 },
    { x: 125, y: 62, at: 4 },
  ];
  return (
    <Scene className={className}>
      {/* the second, shorter tower */}
      <rect x="54" y="58" width="32" height="74" fill="currentColor" fillOpacity="0.04" stroke="currentColor" strokeOpacity="0.55" strokeWidth="1.5" />
      {[70, 84, 98, 112].map((y) => (
        <line key={y} x1="58" y1={y} x2="82" y2={y} stroke="currentColor" strokeOpacity="0.25" strokeWidth="1" />
      ))}
      {/* the curtain-wall tower */}
      <rect x="86" y="14" width="52" height="118" fill="currentColor" fillOpacity="0.06" {...STROKE} />
      {panels.map((p, i) => (
        <rect
          key={i}
          x={p.x}
          y={p.y}
          width="13"
          height="12"
          fill={LIT}
          fillOpacity="0.9"
          className={p.lit ? undefined : HOVER_LIGHT}
          style={p.lit ? undefined : { transitionDelay: `${(p.at ?? 0) * 70}ms` }}
        />
      ))}
      {floors.map((y) => (
        <line key={y} x1="86" y1={y} x2="138" y2={y} stroke="currentColor" strokeOpacity="0.3" strokeWidth="1" />
      ))}
      {[99, 112, 125].map((x) => (
        <line key={x} x1={x} y1="14" x2={x} y2="122" stroke="currentColor" strokeOpacity="0.3" strokeWidth="1" />
      ))}
      {/* the entrance canopy */}
      <path d="M98 122 H126 M104 122 V132 M120 122 V132" fill="none" {...STROKE} strokeWidth="1.5" />
      {/* the annex, with window bands */}
      <rect x="138" y="72" width="36" height="60" fill="currentColor" fillOpacity="0.05" {...STROKE} />
      {[82, 96, 110].map((y, i) => (
        <g key={y}>
          <rect x="144" y={y} width="24" height="6" rx="1" fill="currentColor" fillOpacity="0.14" />
          <rect x="144" y={y} width="24" height="6" rx="1" fill={LIT} className={HOVER_LIGHT} style={{ transitionDelay: `${(i + 5) * 70}ms` }} />
        </g>
      ))}
    </Scene>
  );
}

/** عمارات: a residential building with shops along the street, a tank on the roof. */
export function BuildingScene({ className }: { className?: string }) {
  const cols = [50, 72, 94, 116, 138, 160];
  const rows = [50, 68, 86];
  const atRest = new Set(['94-50', '138-68']);
  return (
    <Scene className={className}>
      {/* the water tank on the roof */}
      <rect x="150" y="27" width="18" height="11" rx="2" fill="currentColor" fillOpacity="0.08" {...STROKE} strokeWidth="1.5" />
      <path d="M153 38 V40 M165 38 V40" fill="none" {...STROKE} strokeWidth="1.25" />
      <rect x="40" y="40" width="144" height="92" fill="currentColor" fillOpacity="0.06" {...STROKE} />
      {rows.flatMap((y, r) => cols.map((x, c) => {
        const key = `${x}-${y}`;
        return <Win key={key} x={x} y={y} w={14} h={10} lit={atRest.has(key)} hover={!atRest.has(key) && (r + c) % 3 === 0} delay={(r * 6 + c) * 25} />;
      }))}
      {/* the shop floor: three fronts under awnings; they light on hover */}
      <line x1="40" y1="102" x2="184" y2="102" {...STROKE} strokeWidth="1.5" />
      {[48, 92, 136].map((x, i) => (
        <g key={x}>
          <path d={`M${x - 2} 108 l3 -5 h38 l3 5 Z`} fill="currentColor" fillOpacity="0.18" {...STROKE} strokeWidth="1.25" />
          <rect x={x} y="110" width="40" height="22" fill="currentColor" fillOpacity="0.12" />
          <rect x={x} y="110" width="40" height="22" fill={LIT} fillOpacity="0.75" className={HOVER_LIGHT} style={{ transitionDelay: `${i * 90}ms` }} />
          <line x1={x + 20} y1="110" x2={x + 20} y2="132" {...STROKE} strokeWidth="1.25" />
        </g>
      ))}
    </Scene>
  );
}

/** استراحات: a rest house behind its wall, a pool and a palm in front. */
export function RestHouseScene({ className }: { className?: string }) {
  return (
    <Scene className={className}>
      {/* the boundary wall with its gate posts */}
      <path d="M12 132 V108 H58 M70 108 H212 V132" fill="none" {...STROKE} strokeOpacity="0.55" />
      <path d="M58 108 V132 M70 108 V132" fill="none" {...STROKE} strokeOpacity="0.55" strokeWidth="2.25" />
      {/* the hall */}
      <rect x="128" y="74" width="66" height="58" fill="currentColor" fillOpacity="0.06" {...STROKE} />
      <line x1="124" y1="74" x2="198" y2="74" {...STROKE} strokeWidth="2.25" />
      <Win x={138} y={86} w={16} h={14} lit />
      <Win x={168} y={86} w={16} h={14} hover delay={80} />
      <rect x="154" y="112" width="14" height="20" rx="1.5" fill="currentColor" fillOpacity="0.16" {...STROKE} strokeWidth="1.5" />
      <rect x="154" y="112" width="14" height="20" rx="1.5" fill={LIT} fillOpacity="0.85" className={HOVER_LIGHT} />
      {/* the pool; its ripple drifts on hover */}
      <rect x="22" y="118" width="88" height="11" rx="5.5" fill="currentColor" fillOpacity="0.2" {...STROKE} strokeWidth="1.5" />
      <path
        d="M30 123.5 q 5 -3 10 0 t 10 0 t 10 0 t 10 0 t 10 0 t 10 0 t 10 0"
        fill="none"
        stroke="currentColor"
        strokeOpacity="0.55"
        strokeWidth="1.25"
        strokeLinecap="round"
        className="transition-transform duration-700 ease-out group-hover:translate-x-1.5 motion-reduce:transition-none"
      />
      <Palm x={112} top={80} />
    </Scene>
  );
}

export const TYPE_SCENES = {
  apartment: ApartmentScene,
  villa: VillaScene,
  land: LandScene,
  office: OfficeScene,
  building: BuildingScene,
  rest_house: RestHouseScene,
} as const;
